import { text as config } from '@config';
import React, { useEffect, useState, useMemo } from 'react';
import ReactAppBuilder from 'src/utils/react-app-builder';
import { OrderedListApp } from '@desygner/ui-editor-popup-menu';
import Store, { actions } from '@store';
import { useSelector } from 'react-redux';
import { calculateAdvanceOrderContent } from 'src/services/text-styling';
import { debounce } from 'src/utils/toolbelt';

class OrderedListMenu extends ReactAppBuilder {
  constructor ({ appId, targetNodeSelector }) {
    super({ appId, targetNodeSelector });

    this.defaultOptions = null;
    this.selectedValues = null;
    this.orderedLevel = null;
    this.orderedStart = null;
    this.orderedStyle = null;
    this.orderedRegex = null;
    this.orderedWidth = null;
    this.orderedNumberingStyle = null;
    this.isContinueFromPrevious = null;
    this.defaultOptions = null;
    this.regexOptions = null;
    this.selectedValues = null;
    this.acrossPages = null;
    this.continueAcrossPagesEditable = null;
    this.listStyles = null;
    this.advancedOrderedStyleFeatureFlag = null;
    this.isOrderedStyleEditable = null;
  }

  closeApp () {
    super.closeApp(this.App);
  }

  mountApp ({
    orderedLevel,
    orderedStart,
    orderedStyle,
    orderedRegex,
    orderedWidth,
    orderedNumberingStyle,
    isContinueFromPrevious,
    element
  }, textarea) {
    if (this.root) this.closeApp();

    const metadata = Store.getState().metadata.bullet_and_ordered_list;
    const level = config.textListOptionMenu.minLevel;
    const listData = metadata?.list_styles ?? {};

    this.element = element;
    this.textarea = textarea; // Will not pass this prop to the app
    this.orderedLevel = orderedLevel; // Will not pass this prop to the app
    this.orderedStart = orderedStart;
    this.orderedStyle = orderedStyle;
    this.orderedRegex = orderedRegex;
    this.orderedWidth = orderedWidth;
    this.orderedNumberingStyle = orderedNumberingStyle;
    this.isContinueFromPrevious = isContinueFromPrevious;

    this.defaultOptions = Array(level).fill(
      config.orderedListOptionMenuOptions.map((option) => ({
        ...option,
        isSelected: false
      }))
    );

    this.regexOptions = config.regexOptions.map(option => ({ value: option.value, label: INKIVE.TranslateHelper.translate(`popupmenu.text_list.${option.key}`) }));
    this.selectedValues = Array(level).fill(this.defaultOptions[0].value);

    this.acrossPages = true;
    this.continueAcrossPagesEditable = true;
    this.listStyles = Store.getState().text.orderedList.styles;

    this.advancedOrderedStyleFeatureFlag = !!INKIVE.C.FEATURE_FLAG.extended_editing?.includes('advanced_list_modals');
    this.isOrderedStyleEditable = false;

    const debounceTime = this.advancedOrderedStyleFeatureFlag ? 200 : 0;
    this.setInput = debounce(this.setInput.bind(this), debounceTime);

    super.mountApp(this.App);
  }

  get labels () {
    return {
      modalTitle: INKIVE.TranslateHelper.translate('popupmenu.text_list.title'),
      continueFromPrevious: INKIVE.TranslateHelper.translate('popupmenu.text_list.continue_from'),
      startFrom: INKIVE.TranslateHelper.translate('popupmenu.text_list.start_from'),
      setNewListStyle: INKIVE.TranslateHelper.translate('popupmenu.text_list.set_new_list_style'),
      listSpacing: INKIVE.TranslateHelper.translate('popupmenu.text_list.listing_spacing'),
      none: INKIVE.TranslateHelper.translate('popupmenu.text_bullets.none'),
      back: INKIVE.TranslateHelper.translate('common.back'),
      levelTitle: INKIVE.TranslateHelper.translate('popupmenu.text_list_option_menu.level'),
      addLevel: INKIVE.TranslateHelper.translate('popupmenu.text_list_option_menu.add_new_level'),
      removeLevel: INKIVE.TranslateHelper.translate('popupmenu.text_list_option_menu.remove'),
      continueAcrossPages: INKIVE.TranslateHelper.translate('popupmenu.text_list_option_menu.continue_number_across_stories'),
      save: INKIVE.TranslateHelper.translate('popupmenu.text_list_option_menu.save'),
      cancel: INKIVE.TranslateHelper.translate('popupmenu.text_list_option_menu.cancel'),
      customBulletModalTitle: INKIVE.TranslateHelper.translate('popupmenu.text_list_option_menu.custom_bullet_modal_title'),
      regexPlaceholder: INKIVE.TranslateHelper.translate('popupmenu.text_list_option_menu.regex_placeholder'),
      tooltipOrderedRegex: INKIVE.TranslateHelper.translate('popupmenu.tooltip.ordered_regex')
    };
  }

  renderTextValues () {
    const textarea = new INKIVE.Text.Textarea().setDOM(this.element, false, true);

    const orderedStart = this.isContinueFromPrevious ? 0 : this.orderedStart;
    if (INKIVE.EditorFacade.canvas.getMode() === 'textedit') {
      const range = INKIVE.EditorFacade.canvas.runExtensions('selectionRange', {});
      const { startLineToStyle, lastLineToStyle } = textarea.text.setLineStyling(range.start, range.end, 'ordered', this.orderedLevel, this.orderedStyle);
      for (let lineIndex = startLineToStyle; lineIndex <= lastLineToStyle; lineIndex++) {
        textarea.text.strings[lineIndex].orderedStart = orderedStart;
        textarea.text.strings[lineIndex].orderedWidth = this.orderedWidth;
        textarea.text.strings[lineIndex].orderedRegex = this.orderedRegex;
        textarea.text.strings[lineIndex].orderedNumberingStyle = this.orderedNumberingStyle;
      }

      INKIVE.EditorFacade.canvas.runExtensions('renderTextArea', { textArea: textarea });
      INKIVE.AdvancedOrderedListFactory.updateTextsInPage(INKIVE.Tool._current_design_id);
      INKIVE.DesignFacade.trigger('text.update', this.orderedLevel);
    } else {
      INKIVE.DesignFacade.set({
        ordered_list: this.orderedLevel,
        ordered_style: this.orderedStyle,
        ordered_start: orderedStart,
        ordered_regex: this.orderedRegex,
        ordered_width: this.orderedWidth,
        ordered_fill: null,
        ordered_numbering_style: this.orderedNumberingStyle
      });

      INKIVE.AdvancedOrderedListFactory.updateTextsInPage(INKIVE.Tool._current_design_id);
    }
  }

  setInput () {
    this.renderTextValues();
  }

  async onSave (values, { acrossPages, regex }) {
    const metadata = INKIVE.AdvancedOrderedListFactory.createNewMetadata(values, acrossPages, regex);

    await Store.dispatch(actions.text.setListStyle(metadata));

    INKIVE.hasEditorUnsavedChanges = true;
  }

  getOrderedListPreview (listStyles) {
    const listStylesPreview = {};
    const numOfLinesToPreview = 4;

    for (const id in listStyles) {
      const style = listStyles[id];
      const orderedArray = Array(numOfLinesToPreview).fill(1);
      const orderedStyleArray = Array(numOfLinesToPreview).fill(Number(id));
      const orderedStartArray = Array(numOfLinesToPreview).fill(0);

      const maxLevel = Math.min(Math.max(style?.pattern?.length, style?.symbols?.length), 3);

      for (let i = 0; i < maxLevel; i++) {
        orderedArray[i] = i + 1;
      }

      const preview = calculateAdvanceOrderContent(orderedArray, orderedStartArray, orderedStyleArray);

      listStylesPreview[id] = { preview };
    }

    return listStylesPreview;
  }

  mapProps () {
    const listStyles = useSelector(state => state.text.orderedList.styles);
    const listStylesPreview = useMemo(() => this.getOrderedListPreview(listStyles), [listStyles]);

    const [orderedStart, setOrderedStart] = useState(this.orderedStart);
    const [orderedStyle, setOrderedStyle] = useState(this.orderedStyle);
    const [orderedRegex, setOrderedRegex] = useState(this.orderedRegex);
    const [orderedWidth, setOrderedWidth] = useState(this.orderedWidth);
    const [orderedNumberingStyle, setOrderedNumberingStyle] = useState(this.orderedNumberingStyle);
    const [isContinueFromPrevious, setContinueFromPrevious] = useState(this.isContinueFromPrevious);
    const [isAdvancedOrderedStyleEnable, toggleAdvancedOrderedStyle] = useState(!!(this.orderedRegex || this.orderedNumberingStyle));

    const regexButtons = [
      { label: '●', regex: '^#.' },
      { label: ')', regex: '^#)' },
      { label: '-', regex: '^#-' },
      { label: this.labels.none, regex: '' }
    ];

    const updateState = (buttonsFlagData) => {
      if (buttonsFlagData.orderedProcessed) return;

      buttonsFlagData.orderedProcessed = true;

      const orderedStart = buttonsFlagData.orderedStart || 1;
      const orderedStyle = buttonsFlagData.orderedStyle || 0;
      const orderedRegex = buttonsFlagData.orderedRegex || '';
      const orderedWidth = buttonsFlagData.orderedWidth || 0;
      const orderedNumberingStyle = config.orderedList.stylesMapping[buttonsFlagData.orderedNumberingStyle] || 'numbers';
      const isAdvancedOrderedStyleEnable = !!(buttonsFlagData.orderedRegex || buttonsFlagData.orderedNumberingStyle);

      setOrderedStart(orderedStart);
      setOrderedStyle(orderedStyle);
      setOrderedRegex(orderedRegex);
      setOrderedWidth(orderedWidth);
      setOrderedNumberingStyle(orderedNumberingStyle);
      setContinueFromPrevious(!buttonsFlagData.orderedStart);
      toggleAdvancedOrderedStyle(isAdvancedOrderedStyleEnable);

      this.orderedStart = orderedStart;
      this.orderedStyle = orderedStyle;
      this.orderedRegex = orderedRegex;
      this.orderedWidth = orderedWidth;
      this.orderedNumberingStyle = buttonsFlagData.orderedNumberingStyle || 0;
    };

    useEffect(() => {
      INKIVE.EditorFacade.off('ordered.selection_changed').on('ordered.selection_changed', updateState);
    }, []);

    return {
      variant: 'ordered-list',
      minLevel: config.textListOptionMenu.minLevel,
      maxLevel: config.textListOptionMenu.maxLevel,
      defaultOptions: this.defaultOptions,
      regexOptions: this.regexOptions,
      selectedValues: this.selectedValues,
      acrossPages: this.acrossPages,
      continueAcrossPagesEditable: this.continueAcrossPagesEditable,
      labels: this.labels,
      listStyles: listStylesPreview,
      orderedStyle,
      orderedRegex,
      regexButtons,
      orderedNumberingStyle,
      orderedStart,
      orderedWidth,
      isContinueFromPrevious,
      isAdvancedOrderedStyleEnable,
      advancedOrderedStyleFeatureFlag: this.advancedOrderedStyleFeatureFlag,
      isOrderedStyleEditable: this.isOrderedStyleEditable,
      setOrderedStyle: (id) => {
        setOrderedStyle(id);
        setOrderedNumberingStyle('numbers');

        this.orderedStyle = id;
        this.orderedLevel = id ? 1 : 0;
        this.orderedNumberingStyle = 0;

        this.renderTextValues();
      },
      toggleAdvancedOrderedStyle: (enable) => {
        if (!enable) {
          setOrderedNumberingStyle('numbers');
          setOrderedRegex('');

          this.orderedRegex = '';
          this.orderedNumberingStyle = 0;

          if (!this.orderedStyle) return;
          this.renderTextValues();
        }
      },
      setOrderedNumberingStyle: (numberingStyle) => {
        const flip = {};
        Object.keys(config.orderedList.stylesMapping).forEach(key => {
          flip[config.orderedList.stylesMapping[key]] = key;
        });

        setOrderedNumberingStyle(numberingStyle);

        this.orderedNumberingStyle = flip[numberingStyle] || 0;

        if (!this.orderedStyle) return;
        this.renderTextValues();
      },
      setOrderedRegex: (pattern) => {
        setOrderedRegex(pattern);

        if (this.orderedRegex === pattern) return;
        this.orderedRegex = pattern;

        if (!this.orderedStyle) return;
        this.setInput();
      },
      setOrderedWidth: (width) => {
        setOrderedWidth(width);

        this.orderedWidth = width;

        if (!this.orderedStyle) return;
        this.renderTextValues();
      },
      setContinueFromPrevious: (isContinueFromPrevious) => {
        setContinueFromPrevious(isContinueFromPrevious);

        this.isContinueFromPrevious = isContinueFromPrevious;

        if (!this.orderedStyle) return;
        this.renderTextValues();
      },
      setOrderedStart: (orderedStart) => {
        setOrderedStart(orderedStart);

        this.orderedStart = orderedStart;

        if (!this.orderedStyle) return;
        this.renderTextValues();
      },
      onSave: (values, { acrossPages, regex }) => {
        this.onSave(values, { acrossPages, regex });
      },
      onCancel: () => console.log('cancel')
    };
  }

  App = () => {
    const props = this.mapProps();

    return <OrderedListApp {...props} />;
  };
}

export default new OrderedListMenu({
  appId: 'react-ordered-list-menu-app',
  targetNodeSelector: 'div.edit-ordered-list'
});
